import { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { useUpdateTeacherStudents } from "../../../../hooks/school/teacher/useUpdateTeacherStudents";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../SchoolAdmin/Teacher/Teacher.css";

/**
 * Modal to bulk-assign a teacher to selected students.
 *
 * States:
 *  - select   : teacher list with search + infinite scroll
 *  - loading  : assigning in progress
 *  - success  : assignment succeeded
 *  - error    : assignment failed, retry / cancel
 */
const AssignTeacherModal = ({
  show,
  handleClose,
  selectedStudentIds = new Set(),
  onSuccess,
}) => {
  const {
    teachers,
    metaData,
    getTeachers,
    loading: teachersLoading,
    error: teachersError,
  } = useGetTeachers();

  const {
    updateTeacherStudents,
    loading: assigning,
    error: assignError,
  } = useUpdateTeacherStudents();

  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const searchTimerRef = useRef(null);

  // Load teachers when modal opens
  useEffect(() => {
    if (show) {
      resetState();
      setPage(1);
      setSearchTerm("");
      getTeachers(1, ITEMS_PER_PAGE, false);
    }
  }, [show]);

  // Watch hook error state
  useEffect(() => {
    if (assignError) {
      setErrorMsg(assignError);
    }
  }, [assignError]);

  const resetState = () => {
    setSelectedTeacherId(null);
    setSuccess(false);
    setErrorMsg("");
  };

  // Search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(1);
      getTeachers(1, ITEMS_PER_PAGE, false, value);
    }, 300);
  };

  // Infinite scroll
  const handleScroll = (e) => {
    const bottomReached =
      e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight - 2;
    const hasMore = metaData?.current_page < metaData?.last_page;

    if (bottomReached && hasMore && !teachersLoading) {
      const next = page + 1;
      setPage(next);
      getTeachers(next, ITEMS_PER_PAGE, true, searchTerm);
    }
  };

  // Assign handler
  const handleAssign = async () => {
    if (!selectedTeacherId) return;
    setErrorMsg("");

    await updateTeacherStudents(
      {
        action: "assign",
        teacher_id: selectedTeacherId,
        student_ids: Array.from(selectedStudentIds),
      },
      () => {
        setSuccess(true);
      },
    );
  };

  const handleRetry = () => {
    handleAssign();
  };

  const handleSuccessContinue = () => {
    onSuccess?.();
    handleClose();
  };

  const handleModalClose = () => {
    if (assigning) return; // prevent close while assigning
    handleClose();
  };

  const studentCount = selectedStudentIds.size;
  const selectedTeacher = teachers?.find((t) => t.id === selectedTeacherId);

  // --- Icon helpers (matching DeleteModal pattern) ---
  const getIcon = () => {
    if (success) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-check-circle-fill text-success fs-2"></i>
        </div>
      );
    }
    if (errorMsg) {
      return (
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10"
          style={{ width: "64px", height: "64px" }}
        >
          <i className="bi bi-x-circle-fill text-danger fs-2"></i>
        </div>
      );
    }
    return null;
  };

  // ---- Render ----
  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      centered
      className="custom-modal"
      backdrop={assigning ? "static" : true}
      keyboard={!assigning}
    >
      <Modal.Body className="p-4">
        {/* ===== SUCCESS STATE ===== */}
        {success && (
          <div className="text-center">
            <div className="mb-3">{getIcon()}</div>
            <h5 className="fw-bold fs-4">Assigned Successfully</h5>
            <p className="text-success mb-4">
              {studentCount} student{studentCount !== 1 ? "s" : ""} assigned to{" "}
              {selectedTeacher
                ? `${selectedTeacher.firstName} ${selectedTeacher.lastName}`
                : "the teacher"}{" "}
              successfully.
            </p>
            <Button
              className="custom-primary-button"
              onClick={handleSuccessContinue}
            >
              Continue
            </Button>
          </div>
        )}

        {/* ===== ERROR STATE ===== */}
        {!success && errorMsg && (
          <div className="text-center">
            <div className="mb-3">{getIcon()}</div>
            <h5 className="fw-bold fs-4">Assignment Failed</h5>
            <p className="text-danger mb-4">{errorMsg}</p>
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
              <Button
                className="custom-cancel-button"
                onClick={handleModalClose}
                disabled={assigning}
              >
                Cancel
              </Button>
              <Button
                className="custom-primary-button"
                onClick={handleRetry}
                disabled={assigning}
              >
                {assigning ? "Retrying..." : "Retry"}
              </Button>
            </div>
          </div>
        )}

        {/* ===== SELECT TEACHER STATE ===== */}
        {!success && !errorMsg && (
          <>
            <h5 className="fw-bold mb-1">Assign Teacher</h5>
            <p className="text-muted mb-3" style={{ fontSize: "14px" }}>
              {studentCount} student{studentCount !== 1 ? "s" : ""} selected
            </p>

            <label className="mb-1 fw-semibold" style={{ fontSize: "14px" }}>
              Select Teacher
            </label>

            {/* Search */}
            <div className="custom-dropdown-container">
              <div className="custom-dropdown-options" style={{ position: "relative" }}>
                <div className="custom-dropdown-search">
                  <input
                    type="text"
                    className="custom-dropdown-search-input"
                    placeholder="Search teacher..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>

                {/* Scrollable teacher list */}
                <div
                  className="custom-dropdown-scroll-area"
                  style={{ maxHeight: "220px" }}
                  onScroll={handleScroll}
                >
                  {teachersLoading && (!teachers || teachers.length === 0) ? (
                    <div className="custom-dropdown-option disabled">
                      Loading...
                    </div>
                  ) : (
                    <>
                      {teachersError && (
                        <div className="custom-dropdown-option disabled text-danger">
                          {teachersError}
                        </div>
                      )}

                      {!teachersError &&
                        (!teachers || teachers.length === 0) && (
                          <div className="custom-dropdown-option disabled">
                            No teachers found
                          </div>
                        )}

                      {teachers?.map((teacher) => (
                        <div
                          key={teacher.id}
                          className="custom-dropdown-option"
                          style={{
                            background:
                              selectedTeacherId === teacher.id
                                ? "#e7f3ff"
                                : undefined,
                            fontWeight:
                              selectedTeacherId === teacher.id
                                ? "600"
                                : undefined,
                          }}
                          onClick={() => setSelectedTeacherId(teacher.id)}
                        >
                          {teacher.firstName} {teacher.lastName}
                          {selectedTeacherId === teacher.id && (
                            <i className="bi bi-check-lg ms-2 text-primary"></i>
                          )}
                        </div>
                      ))}

                      {teachersLoading && teachers && teachers.length > 0 && (
                        <div className="custom-dropdown-option disabled">
                          Loading more...
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="d-flex justify-content-end gap-3 mt-3">
              <Button
                className="custom-cancel-button"
                onClick={handleModalClose}
                disabled={assigning}
              >
                Cancel
              </Button>
              <Button
                className="custom-primary-button"
                onClick={handleAssign}
                disabled={!selectedTeacherId || assigning}
              >
                {assigning ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AssignTeacherModal;
